import React, { memo, useMemo } from 'react';
import { batch } from 'react-redux';
import {
  activeSchemaIdSelector,
  errorPrimitiveSelector,
  eventConditionsPrimitiveSelector,
  eventPropertyPrimitiveSelector,
  functionPrimitiveSelector,
} from '../../../redux/selectors/builder';
import {
  IRowMeta,
  removeErrorAction,
  setEventPropertyAction,
} from '../../../redux/slices/oldBuilder';
import {
  FieldContext,
  IEventInput,
  TagOption,
} from '../../../utils/apollo/graphqlSchema';
import {
  defaultEventInput,
  isEmptyEventCondition,
} from '../../../utils/builder';
import EntitySelection from '../EntitySelection';
import {
  asSingleSelection,
  createTagOptionInput,
} from '../EntitySelection/utils';
import {
  IRemoveEntityHandler,
  ISelectEntityHandler,
} from '../EntitySelection/types';
import { assertCondition } from '../../../utils/types';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import * as BuilderUI from '../BuilderUI';
import FieldDroppableZone from '../../DndEntity/DndDroppableZone/FieldDroppableZone';
import { createDictionaryEntityDropzoneId } from '../../DndEntity/utils';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import {
  EntityDropzoneDataTypeEnum,
  IPropertyEntityDropzoneDataType,
} from '../../DndEntity/validation';

interface IProps {
  rowMeta: IRowMeta;
  eventsInput?: IEventInput[];
}

export default memo(({ rowMeta, eventsInput }: IProps) => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  // Prepare selectors
  const functionSelector = useMemo(functionPrimitiveSelector, []);
  const eventConditionSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventPropertySelector = useMemo(eventPropertyPrimitiveSelector, []);
  // Prepare dispatch
  const dispatch = useAppDispatch();
  // Selectors
  const eventConditions = useAppSelector((state) =>
    eventConditionSelector(state, rowMeta),
  );
  // builder property only happen on first event condition in Query Builder
  assertCondition(eventConditions.length === 1);
  const eventCondition = eventConditions[0];
  const rowFn = useAppSelector((state) => functionSelector(state, rowMeta));
  const currentEventProperty = useAppSelector((state) =>
    eventPropertySelector(state, rowMeta),
  );

  const errorKey = 'fields';

  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const errors = useAppSelector((state) =>
    errorSelector(state, rowMeta, errorKey),
  );
  const emptyEventCondition = isEmptyEventCondition(eventCondition);
  const handlePropertySelection: ISelectEntityHandler = (property) => {
    assertCondition(property.__typename === 'Field');
    batch(() => {
      dispatch(
        setEventPropertyAction({
          rowMeta,
          property,
        }),
      );
      dispatch(removeErrorAction({ rowMeta, errorKey }));
    });
  };

  const handleRemoveProperty: IRemoveEntityHandler = () => {
    batch(() => {
      dispatch(
        setEventPropertyAction({
          rowMeta,
          property: null,
        }),
      );
      dispatch(removeErrorAction({ rowMeta, errorKey }));
    });
  };

  const tags = useMemo(
    () => [
      createTagOptionInput(TagOption.Field, {
        field: {
          func: rowFn.key,
          context: FieldContext.Measure,
          events: eventsInput ?? [defaultEventInput],
          arrayObjectContext: null,
        },
      }),
    ],
    [eventsInput, rowFn.key],
  );

  const dropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Fields,
    rowMeta,
    '-Property',
  );

  return (
    <BuilderUI.BuilderFieldContainer>
      <BuilderUI.BuilderFieldLabel>Property</BuilderUI.BuilderFieldLabel>
      <FieldDroppableZone
        dropzoneId={dropzoneId}
        tags={tags}
        data={
          {
            id: dropzoneId,
            type: EntityDropzoneDataTypeEnum.Property,
            rowMeta,
            errorKey,
          } satisfies IPropertyEntityDropzoneDataType
        }
        hasError={emptyEventCondition}
      >
        <EntitySelection
          selectedEntities={asSingleSelection(currentEventProperty)}
          schemaId={activeSchemaId}
          tags={tags}
          onSelectEntity={handlePropertySelection}
          onRemoveEntity={handleRemoveProperty}
          isError={Boolean(errors)}
          isDisabled={emptyEventCondition}
        />
      </FieldDroppableZone>
    </BuilderUI.BuilderFieldContainer>
  );
});
